import { useEffect, useState } from "react";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { getComponentFromPath } from "@kaltura/mediaspace-shared-utils";

interface PreSaveComponentProps {
    kmsComponents: KmsTypeComponent[];
    display: boolean;
    formData: { entryIds: string[]; data: any };
    saveConfirmationResolvePromise: ((value: { confirm: boolean; isEditable: boolean }) => void) | null;
    setDisplayPreSaveComponent: (display: boolean) => void;
}

/**
 * Runs pre-save components sequentially (PHP-sorted by order).
 * Cancel aborts the remaining chain. isEditable is AND-aggregated across confirms.
 */
export const PreSaveComponent = ({
    kmsComponents,
    display,
    formData,
    saveConfirmationResolvePromise,
    setDisplayPreSaveComponent,
}: PreSaveComponentProps) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [aggregatedIsEditable, setAggregatedIsEditable] = useState(true);

    useEffect(() => {
        if (display) {
            setActiveIndex(0);
            setAggregatedIsEditable(true);
        }
    }, [display]);

    const finish = (confirm: boolean, isEditable: boolean) => {
        if (saveConfirmationResolvePromise) {
            saveConfirmationResolvePromise({
                confirm,
                isEditable,
            });
        }
        setDisplayPreSaveComponent(false);
        setActiveIndex(0);
        setAggregatedIsEditable(true);
    };

    const handleConfirm = (isEditable: boolean) => {
        const nextIsEditable = aggregatedIsEditable && isEditable;
        const nextIndex = activeIndex + 1;

        if (nextIndex >= kmsComponents.length) {
            finish(true, nextIsEditable);
            return;
        }

        setAggregatedIsEditable(nextIsEditable);
        setActiveIndex(nextIndex);
    };

    const handleCancel = () => {
        finish(false, true);
    };

    if (!display || !kmsComponents.length) {
        return null;
    }

    const kmsComponent = kmsComponents[activeIndex];
    if (!kmsComponent) {
        return null;
    }

    const Component = getComponentFromPath(kmsComponent.component);

    return (
        <ErrorBoundary key={`${kmsComponent.name}-${activeIndex}`} fallback={<></>}>
            <Component
                {...{
                    ...kmsComponent.props,
                    display: display,
                    formData: formData,
                    onConfirm: handleConfirm,
                    onCancel: handleCancel,
                }}
            />
        </ErrorBoundary>
    );
};
